type Bank = {clips:Record<string,{offset:number;duration:number}>;groups:Record<string,string[]>};
export type SoundPoint = {x:number;z:number};
type Voice = {source:AudioBufferSourceNode;volume:GainNode;priority:number;key:string};
export type CleopatraLine = 'select'|'bath'|'super'|'win'|'lose';

/** One decoded sprite, a bounded voice pool and no server-side audio work. */
export class SoundMixer {
  private context:AudioContext|null=null;
  private master:GainNode|null=null;
  private buffer:AudioBuffer|null=null;
  private bank:Bank|null=null;
  private museumMusicActive=false;
  private museumMusicLoading:Promise<AudioBuffer|null>|null=null;
  private museumMusic:AudioBufferSourceNode|null=null;
  private museumMusicGain:GainNode|null=null;
  private loading:Promise<void>|null=null;
  private voices=new Set<Voice>();
  private dialogueClips=new Map<CleopatraLine,Promise<AudioBuffer>>();
  private dialogue: {priority:number;source?:AudioBufferSourceNode}|null=null;
  private cooldown=new Map<string,number>();
  private lastClip=new Map<string,string>();
  private focus:SoundPoint|null=null;
  private right:SoundPoint={x:1,z:0};
  enabled=true;
  volume=.55;
  constructor(){
    try {this.enabled=localStorage.getItem('arena-audio-muted')!=='1';this.volume=Math.max(0,Math.min(1,Number(localStorage.getItem('arena-audio-volume')??.55)));if(!Number.isFinite(this.volume))this.volume=.55;}catch{/* Storage may be disabled. */}
    const unlock=()=>{if(this.enabled)void this.unlock();};
    window.addEventListener('pointerdown',unlock,{capture:true});
    window.addEventListener('keydown',unlock,{capture:true});
    document.addEventListener('visibilitychange',()=>{if(document.hidden){this.stop();void this.context?.suspend().catch(()=>{});}else if(this.enabled && this.context)void this.unlock();});
  }
  async unlock():Promise<void>{
    try {
      if(!this.context){
        this.context=new AudioContext({latencyHint:'interactive'});
        this.master=this.context.createGain();
        const limiter=this.context.createDynamicsCompressor();
        limiter.threshold.value=-12;limiter.knee.value=12;limiter.ratio.value=8;limiter.attack.value=.003;limiter.release.value=.12;
        this.master.connect(limiter);limiter.connect(this.context.destination);this.applyVolume();
      }
      if(this.context.state!=='running'&&this.context.state!=='closed')await this.context.resume();
      if(this.museumMusicActive)void this.startMuseumMusic();
      if(!this.loading){
        const context=this.context;
        this.loading=(async()=>{
          const [manifest,wav]=await Promise.all([fetch('/audio/arena-sfx.json'),fetch('/audio/arena-sfx.wav')]);
          if(!manifest.ok||!wav.ok)throw Error('Sound bank unavailable');
          const bank=await manifest.json() as Bank;
          const buffer=await context.decodeAudioData(await wav.arrayBuffer());
          this.bank=bank;this.buffer=buffer;
          if(import.meta.env.DEV)console.info(`[audio] ${Object.keys(bank.clips).length} samples decoded, ${buffer.duration.toFixed(1)}s`);
        })().catch(()=>{this.loading=null;});
      }
      await this.loading;
    }catch{/* Audio is optional; a failed device or download must not interrupt a match. */}
  }
  setMuseumMusic(active:boolean):void{
    if(this.museumMusicActive===active)return;
    this.museumMusicActive=active;
    if(active){
      void this.startMuseumMusic();
    }else if(this.museumMusic&&this.museumMusicGain&&this.context){
      const now=this.context.currentTime;
      this.museumMusicGain.gain.setTargetAtTime(0,now,.18);
      this.museumMusic.stop(now+.8);
      this.museumMusic=null;
      this.museumMusicGain=null;
    }
  }
  private async startMuseumMusic():Promise<void>{
    const context=this.context;
    const master=this.master;
    if(!context||!master||!this.enabled||this.museumMusic||document.hidden)return;
    this.museumMusicLoading??=fetch('/audio/museum-rock.wav').then(response=>{
      if(!response.ok)throw new Error('Museum music unavailable');
      return response.arrayBuffer();
    }).then(bytes=>context.decodeAudioData(bytes)).catch(()=>null);
    const buffer=await this.museumMusicLoading;
    if(!buffer||!this.museumMusicActive||!this.enabled||this.museumMusic||document.hidden)return;
    const source=context.createBufferSource();
    const gain=context.createGain();
    source.buffer=buffer;
    source.loop=true;
    gain.gain.value=0;
    source.connect(gain);
    gain.connect(master);
    source.onended=()=>{source.disconnect();gain.disconnect();};
    source.start();
    gain.gain.setTargetAtTime(.28,context.currentTime,.2);
    this.museumMusic=source;
    this.museumMusicGain=gain;
  }
  setEnabled(value:boolean):void{this.enabled=value;this.save();this.applyVolume();if(!value)this.stop();else void this.unlock();}
  setVolume(value:number):void{this.volume=Math.max(0,Math.min(1,value));this.save();this.applyVolume();}
  private save():void{try{localStorage.setItem('arena-audio-muted',this.enabled?'0':'1');localStorage.setItem('arena-audio-volume',String(this.volume));}catch{/* optional */}}
  private applyVolume():void{if(this.master&&this.context)this.master.gain.setTargetAtTime(this.enabled?this.volume:0,this.context.currentTime,.02);}
  listener(focus:SoundPoint|null,right:SoundPoint):void{this.focus=focus;this.right={x:right.x,z:right.z};}
  stop():void{this.dialogue?.source?.stop();this.dialogue=null;for(const voice of this.voices)try{voice.source.stop();}catch{/* already ended */}this.voices.clear();this.cooldown.clear();}
  async cleopatraLine(line:CleopatraLine,priority=1):Promise<void>{
    const context=this.context,master=this.master;
    if(!this.enabled||document.hidden||context?.state!=='running'||!master)return;
    if(this.dialogue&&this.dialogue.priority>priority)return;
    this.dialogue?.source?.stop();
    const request:{priority:number;source?:AudioBufferSourceNode}={priority};
    this.dialogue=request;
    const started=performance.now();
    let clip=this.dialogueClips.get(line);
    if(!clip){
      clip=fetch(`/audio/cleopatra-${line}.mp3`).then(response=>{
        if(!response.ok)throw new Error('Dialogue unavailable');
        return response.arrayBuffer();
      }).then(bytes=>context.decodeAudioData(bytes));
      this.dialogueClips.set(line,clip);
    }
    try{
      const buffer=await clip;
      if(this.dialogue!==request)return;
      if(!this.enabled||document.hidden||(priority<3&&line!=='select'&&performance.now()-started>1800)){
        this.dialogue=null;return;
      }
      const source=context.createBufferSource();
      const gain=context.createGain();
      source.buffer=buffer;
      gain.gain.value=.8;
      source.connect(gain);
      gain.connect(master);
      source.onended=()=>{
        source.disconnect();
        gain.disconnect();
        if(this.dialogue===request)this.dialogue=null;
      };
      request.source=source;
      source.start();
    }catch{
      this.dialogueClips.delete(line);
      if(this.dialogue===request)this.dialogue=null;
    }
  }
  /** Fades out what still plays under `key`, such as the charge of an interrupted Super Slam. */
  fade(key:string):void{
    const context=this.context;if(!context)return;
    for(const voice of this.voices)if(voice.key===key){voice.volume.gain.setTargetAtTime(0,context.currentTime,.04);try{voice.source.stop(context.currentTime+.2);}catch{/* already ended */}}
  }
  play(group:string,at?:SoundPoint,options:{gain?:number;rate?:number;priority?:number;key?:string;gap?:number}={}):void{
    const context=this.context,bank=this.bank,buffer=this.buffer;
    // Never queue old combat events while a browser is unlocking or downloading.
    if(!this.enabled||document.hidden||context?.state!=='running'||!bank||!buffer||!this.master)return;
    const variants=bank.groups[group];if(!variants?.length)return;
    const now=performance.now(),key=options.key??group,gap=options.gap??45;
    if(now-(this.cooldown.get(key)??-Infinity)<gap)return;
    let gain=options.gain??.65,pan=0;
    if(at&&this.focus){const dx=at.x-this.focus.x,dz=at.z-this.focus.z,dist=Math.hypot(dx,dz);if(dist>32)return;gain*=1/(1+Math.max(0,dist-3)*.12);pan=Math.max(-.85,Math.min(.85,(dx*this.right.x+dz*this.right.z)/14));}
    const priority=options.priority??1;
    if(this.voices.size>=14){const weakest=[...this.voices].sort((a,b)=>a.priority-b.priority)[0]!;if(weakest.priority>priority)return;weakest.source.stop();this.voices.delete(weakest);}
    const choices=variants.filter(id=>id!==this.lastClip.get(group));
    const id=choices[Math.floor(Math.random()*choices.length)]??variants[0]!;const clip=bank.clips[id];if(!clip)return;
    this.lastClip.set(group,id);this.cooldown.set(key,now);
    if(this.cooldown.size>512)for(const [k,t]of this.cooldown)if(now-t>10000)this.cooldown.delete(k);
    const source=context.createBufferSource(),volume=context.createGain(),panner=context.createStereoPanner();
    source.buffer=buffer;source.playbackRate.value=(options.rate??1)*(.96+Math.random()*.08);
    volume.gain.value=gain;panner.pan.value=pan;source.connect(volume);volume.connect(panner);panner.connect(this.master);
    const voice={source,volume,priority,key};this.voices.add(voice);
    source.onended=()=>{this.voices.delete(voice);source.disconnect();volume.disconnect();panner.disconnect();};
    source.start(0,clip.offset,clip.duration);
  }
}
